import type { Stripe } from '@stripe/stripe-js'
import { syncRef } from '@vueuse/core'
import { type Ref, computed, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { useCheckoutStore } from '@/stores/checkout'
import { ErrorData, blankError, clearError } from '@/utils/errors'

const POLLING_INTERVAL_MS = 3000
const POLLING_TIMEOUT_MS = 120000

export function useStripePaymentStatus(options: {
  error: Ref<ErrorData>
  stripe: Ref<Stripe | null>
}) {
  const error = ref<ErrorData>(blankError())
  syncRef(error, options.error, { direction: 'ltr' })
  const checkoutStore = useCheckoutStore()
  const hasPaidExternally = computed<boolean>(
    () =>
      Boolean(route.query.payment_intent_client_secret) &&
      Boolean(route.query.reservation_id) &&
      Boolean(route.query.reservation_key),
  )

  // Verify status
  const { stripe } = options
  const isVerifyingPayment = ref<boolean>(false)
  const route = useRoute()
  async function verifyPayment() {
    const clientSecret = route.query.payment_intent_client_secret as string | undefined
    const reservationId = route.query.reservation_id as string | undefined
    const reservationKey = route.query.reservation_key as string | undefined
    if (!clientSecret || !reservationId || !reservationKey) return
    isVerifyingPayment.value = true
    clearError(error.value)

    if (!stripe.value) {
      error.value.message = 'Could not connect to payment processor. Please refresh and try again.'
      isVerifyingPayment.value = false
      return
    }

    const stripeResponse = await stripe.value.retrievePaymentIntent(clientSecret)
    if (stripeResponse.error) {
      error.value.message = stripeResponse.error.message || ''
      error.value.code = stripeResponse.error.code || ''
      error.value.extra = stripeResponse.error
    } else if (stripeResponse.paymentIntent) {
      switch (stripeResponse.paymentIntent.status) {
        case 'succeeded':
          await checkoutStore.onSuccessfulPurchase()
          break
        case 'processing':
          await pollForPaymentStatus(clientSecret)
          break
        case 'requires_payment_method':
          error.value.message = 'Payment failed. Please try another payment method.'
          break
        default:
          error.value.message = 'An unexpected error occurred. Please try again.'
          break
      }
    }
    isVerifyingPayment.value = false
  }

  // Processing status
  const isPaymentProcessing = ref<boolean>(false)
  let pollingInterval: number | undefined
  let pollingTimeout: number | undefined
  onUnmounted(() => {
    clearInterval(pollingInterval)
    clearTimeout(pollingTimeout)
  })
  async function pollForPaymentStatus(clientSecret: string): Promise<void> {
    isPaymentProcessing.value = true
    error.value.message = "Your payment is processing. We'll update you when it's complete."
    pollingInterval = window.setInterval(async () => {
      const { paymentIntent } = await stripe.value!.retrievePaymentIntent(clientSecret)
      if (paymentIntent?.status === 'succeeded') {
        clearInterval(pollingInterval)
        clearTimeout(pollingTimeout)
        await checkoutStore.onSuccessfulPurchase()
      } else if (paymentIntent?.status === 'requires_payment_method') {
        clearInterval(pollingInterval)
        clearTimeout(pollingTimeout)
        isPaymentProcessing.value = false
        error.value.message = 'Payment failed. Please try another payment method.'
      }
    }, POLLING_INTERVAL_MS)

    pollingTimeout = window.setTimeout(() => {
      clearInterval(pollingInterval)
      isPaymentProcessing.value = false
      error.value.message =
        'Your payment is still processing. Please check your email for a confirmation or contact us for assistance.'
    }, POLLING_TIMEOUT_MS)
  }

  return {
    error,
    isPaymentProcessing,
    isVerifyingPayment,
    verifyPayment,
    hasPaidExternally,
  }
}
